
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ListFilter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

// Import our refactored components
import RecebimentosTabHeader from "../recebimentos/RecebimentosTabHeader";
import RecebimentosList from "../recebimentos/RecebimentosList";
import EmptyRecebimentosList from "../recebimentos/EmptyRecebimentosList";
import RecebimentoConfirmDialog from "../recebimentos/RecebimentoConfirmDialog";
import RecebimentoDeleteDialog from "../recebimentos/RecebimentoDeleteDialog";
import { recebimentosPendentes, recebimentosRecentes } from "../recebimentos/RecebimentosData";

const RecebimentosTabContent: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("pendentes");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedRecebimento, setSelectedRecebimento] = useState<any>(null);

  const handleAgendarRecebimento = () => {
    navigate("/financeiro/recebimentos/agendar");
  };

  const handleRegistrarRecebimento = () => {
    navigate("/financeiro/recebimentos/receber");
  };

  const handleReceberPagamento = (recebimento: any) => {
    setSelectedRecebimento(recebimento);
    setShowConfirmDialog(true);
  };

  const handleEditarRecebimento = (recebimento: any) => {
    // Em uma implementação real, você redirecionaria para a página de edição
    navigate(`/financeiro/recebimentos/editar/${recebimento.id}`);
    toast.info(`Editando recebimento: ${recebimento.descricao}`);
  };

  const handleExcluirRecebimento = (recebimento: any) => {
    setSelectedRecebimento(recebimento);
    setShowDeleteDialog(true);
  };

  const confirmarRecebimento = () => {
    // Aqui implementaria a lógica para registrar o recebimento
    console.log("Recebimento confirmado:", selectedRecebimento);
    setShowConfirmDialog(false);
    toast.success(`Recebimento de ${selectedRecebimento.descricao} confirmado com sucesso!`);
    // Atualizar o estado ou fazer uma chamada de API
  };

  const confirmarExclusao = () => {
    // Aqui implementaria a lógica para excluir o recebimento
    console.log("Recebimento excluído:", selectedRecebimento);
    setShowDeleteDialog(false);
    toast.success(`Recebimento de ${selectedRecebimento.descricao} excluído com sucesso!`);
    // Atualizar o estado ou fazer uma chamada de API
  };

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
        <RecebimentosTabHeader 
          onAgendarRecebimento={handleAgendarRecebimento}
          onRegistrarRecebimento={handleRegistrarRecebimento}
        />

        <Tabs defaultValue="pendentes" value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6 border-b border-gray-200">
            <TabsTrigger 
              value="pendentes" 
              className={`px-4 py-2 border-b-2 transition-colors ${
                activeTab === "pendentes" 
                  ? "border-blue-500 text-blue-500" 
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Pendentes
            </TabsTrigger>
            <TabsTrigger 
              value="historico" 
              className={`px-4 py-2 border-b-2 transition-colors ${
                activeTab === "historico" 
                  ? "border-blue-500 text-blue-500" 
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Histórico
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="pendentes">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-normal text-gray-700">Recebimentos Pendentes</h3>
                <Button variant="ghost" size="sm" className="text-gray-500">
                  <ListFilter className="h-4 w-4 mr-2 text-blue-400" /> Filtrar
                </Button>
              </div>
              
              {recebimentosPendentes.length > 0 ? (
                <RecebimentosList 
                  recebimentos={recebimentosPendentes}
                  onEdit={handleEditarRecebimento}
                  onDelete={handleExcluirRecebimento}
                  onReceber={handleReceberPagamento}
                  tipoLista="pendentes"
                />
              ) : (
                <EmptyRecebimentosList 
                  tipoLista="pendentes" 
                  onAgendar={handleAgendarRecebimento}
                />
              )}
            </div>
          </TabsContent>
          
          <TabsContent value="historico">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-normal text-gray-700">Recebimentos Recentes</h3>
                <Button variant="ghost" size="sm" className="text-gray-500">
                  <ListFilter className="h-4 w-4 mr-2 text-blue-400" /> Filtrar
                </Button>
              </div>
              
              {recebimentosRecentes.length > 0 ? (
                <RecebimentosList 
                  recebimentos={recebimentosRecentes}
                  onEdit={handleEditarRecebimento}
                  onDelete={handleExcluirRecebimento}
                  tipoLista="historico"
                />
              ) : (
                <EmptyRecebimentosList tipoLista="historico" />
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Diálogos de confirmação */}
      <RecebimentoConfirmDialog
        open={showConfirmDialog}
        onOpenChange={setShowConfirmDialog}
        recebimento={selectedRecebimento}
        onConfirm={confirmarRecebimento}
      />

      <RecebimentoDeleteDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        recebimento={selectedRecebimento}
        onConfirm={confirmarExclusao}
      />
    </div>
  );
};

export default RecebimentosTabContent;
